import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { trackScrapeRequest } from "../utils/analytics";
import { fetchScrapeStatus, fetchScrapedData, triggerScrape } from "../api";
import RequireAdmin from "./RequireAdmin";

// Admin-only page for kicking off a scrape by hand. Access is handled by
// RequireAdmin; the real enforcement is server-side on GET /scrape.
function ScrapeStarterInner({ visitorCount, me }) {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");
  const [lastDuration, setLastDuration] = useState("");
  const [status, setStatus] = useState("");
  const [showLog, setShowLog] = useState(false);
  const [scrapeLog, setScrapeLog] = useState(null);

  useEffect(() => {
    document.title = "Admin | Sydney Roster Scheduler";
  }, []);

  const fetchLastUpdated = async () => {
    try {
      const data = await fetchScrapedData();
      const ts =
        data?.no5?.timestamp ||
        data?.ginza?.timestamp ||
        data?.ginza479?.timestamp ||
        data?.nightshade?.timestamp ||
        "";
      if (ts) setLastUpdated(new Date(ts).toLocaleString());

      const scrapeStatus = await fetchScrapeStatus();
      setLastDuration(scrapeStatus?.duration_human || "");
    } catch (e) {
      setLastUpdated("");
      setLastDuration("");
    }
  };

  const fetchLog = async () => {
    try {
      const res = await fetchScrapeStatus();
      setScrapeLog(res);
    } catch (e) {
      setScrapeLog({ status: "unknown", message: "Unable to load log." });
    }
  };

  useEffect(() => {
    fetchLastUpdated();
    fetchLog();
  }, []);

  // Poll for background scrape status
  useEffect(() => {
    let intervalId;

    const checkStatus = async () => {
      try {
        const res = await fetchScrapeStatus();
        if (res.status === "in_progress") {
          setMessage(res.message);
          setLoading(true);
        } else if (res.status === "completed") {
          setMessage(res.message);
          setLastDuration(res.duration_human || "");
          setLoading(false);
          setStatus("completed");
          fetchLastUpdated();
          fetchLog();
        } else if (res.status === "failed") {
          setMessage(res.message);
          setLastDuration(res.duration_human || "");
          setLoading(false);
          setStatus("error");
          fetchLog();
        }
      } catch (e) {
        console.error("Failed to fetch status:", e);
      }
    };

    if (loading && status !== "completed" && status !== "error") {
      intervalId = setInterval(checkStatus, 5000);
    } else {
      checkStatus();
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [loading, status]);

  const handleStartScrape = async () => {
    setLoading(true);
    setStatus("in_progress");
    setMessage("Scraping started in the background! Please keep this page open to monitor progress.");
    setShowLog(true);

    trackScrapeRequest();

    try {
      await triggerScrape();
    } catch (error) {
      setMessage("Failed to start scraping: " + (error.message || "Unknown error"));
      setLoading(false);
      setStatus("error");
    }
  };

  return (
    <div className="container mt-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Start Scraping</h2>
        <div className="d-flex gap-2 align-items-center">
          <span className="badge bg-success">admin: {me?.email}</span>
          <Link to="/admin/subscribers" className="btn btn-sm btn-outline-primary">
            Subscribers →
          </Link>
        </div>
      </div>
      <button className="btn btn-primary" onClick={handleStartScrape} disabled={loading}>
        {loading ? "Scraping in Progress..." : "Start Scraping"}
      </button>
      {message && (
        <div className={`alert mt-3 ${status === 'error' ? 'alert-danger' : status === 'completed' ? 'alert-success' : 'alert-info'}`}>
          {status === 'in_progress' && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
          {message}
        </div>
      )}
      <div className="d-flex gap-2 mt-3">
        <button className="btn btn-outline-secondary btn-sm" onClick={async () => { setShowLog(!showLog); if (!showLog) await fetchLog(); }}>
          {showLog ? "Hide Log" : "Show Log"}
        </button>
      </div>
      {showLog && scrapeLog && (
        <div className="card mt-3">
          <div className="card-header d-flex justify-content-between align-items-center">
            <strong>Latest Scrape Log</strong>
            <small className="text-muted text-uppercase">{scrapeLog.status || "unknown"}</small>
          </div>
          <div className="card-body">
            <p className="mb-2"><strong>Message:</strong> {scrapeLog.message || "No message available."}</p>
            {scrapeLog.started_at && <p className="mb-1"><strong>Started:</strong> {new Date(scrapeLog.started_at).toLocaleString()}</p>}
            {scrapeLog.completed_at && <p className="mb-1"><strong>Completed:</strong> {new Date(scrapeLog.completed_at).toLocaleString()}</p>}
            {scrapeLog.failed_at && <p className="mb-1"><strong>Failed:</strong> {new Date(scrapeLog.failed_at).toLocaleString()}</p>}
            {scrapeLog.duration_human && <p className="mb-1"><strong>Duration:</strong> {scrapeLog.duration_human}</p>}
            {Array.isArray(scrapeLog.log_entries) && scrapeLog.log_entries.length > 0 && (
              <div className="mt-3">
                <strong>Steps</strong>
                <ul className="mt-2 mb-0">
                  {scrapeLog.log_entries.map((entry, idx) => (
                    <li key={`${entry.timestamp || idx}-${idx}`}>
                      <small className="text-muted">{entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString() : ''}</small>
                      {' '}
                      {entry.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
      {lastUpdated && (
        <div className="alert alert-secondary mt-3">
          <strong>Last updated:</strong> {lastUpdated}
          {lastDuration ? ` (Last scrape took ${lastDuration})` : ""}
        </div>
      )}
      {visitorCount !== null && visitorCount !== undefined && (
        <p className="mt-3" style={{ color: 'var(--text-secondary)' }}>👀 {visitorCount.toLocaleString()} visitors</p>
      )}
    </div>
  );
}

function ScrapeStarter({ visitorCount }) {
  return (
    <RequireAdmin title="Admin area">
      {(me) => <ScrapeStarterInner visitorCount={visitorCount} me={me} />}
    </RequireAdmin>
  );
}

export default ScrapeStarter;
